/* eslint-disable no-use-before-define */
import _isNil from 'lodash/isNil';
import _round from 'lodash/round';
import _toFinite from 'lodash/toFinite';
import _toLower from 'lodash/toLower';
import _map from 'lodash/map';
import _toUpper from 'lodash/toUpper';
// serializr
import {
  SKIP, primitive, custom, list, object,
  createSimpleSchema, serialize, deserialize,
  alias,
} from 'serializr';

const paginatedSchemaOf = (schema) => createSimpleSchema({
  data: alias('entries', list(object(schema))),
  pageNumber: custom(() => SKIP, (v) => _toFinite(v)),
  pageSize: custom(() => SKIP, (v) => _toFinite(v)),
  totalEntries: custom(() => SKIP, (v) => _toFinite(v)),
  totalPages: custom(() => SKIP, (v) => _toFinite(v)),
});

// ---- Scalar types ---- //
const ignore = () => SKIP;

const deserializePercentage = (v) => (_isNil(v) ? undefined : _toFinite(v) / 100);
export const serializePercentage = (v) => (_isNil(v) ? undefined : v * 100);

const deserializeAmount = (v) => (_isNil(v) ? undefined : _toFinite(v) / 100);
const serializeAmount = (v) => (_isNil(v) ? undefined : _round(v * 100));

// ---- Schemas placed on top because of dependencies ---- //
// Address & location
const addressSchema = createSimpleSchema({
  city: primitive(),
  country: primitive(),
  extra: primitive(),
  street: primitive(),
  zipCode: primitive(),
});

const locationSchema = createSimpleSchema({
  latitude: custom((v) => v, (v) => _toFinite(v)),
  longitude: custom((v) => v, (v) => _toFinite(v)),
});

// Device
const deviceSchema = createSimpleSchema({
  disabled: primitive(),
  id: custom((v) => v, (v) => _toFinite(v)),
  name: primitive(),
  serial: primitive(),
  timeout: primitive(),
  lastCommDate: primitive(),
});

// Terminal
const paymentReaderSchema = createSimpleSchema({
  chargingStatus: custom((v) => v, (v) => _toLower(v)),
  connectionStatus: custom((v) => v, (v) => _toLower(v)),
  creationDate: primitive(),
  firmwareVersion: primitive(),
  id: primitive(),
  serialNumber: primitive(),
});

export const terminalSchema = createSimpleSchema({
  appStatus: custom((v) => v, (v) => _toLower(v)),
  appVersion: primitive(),
  batteryLevel: custom(() => SKIP, (v) => _toFinite(v)),
  chargingStatus: custom((v) => v, (v) => _toLower(v)),
  creationDate: primitive(),
  id: primitive(),
  kioskSecret: primitive(),
  lastCommDate: primitive(),
  paymentReaders: list(object(paymentReaderSchema)),
  terminalIdentifier: primitive(),
  timeout: primitive(),
  type: custom((v) => v, (v) => _toLower(v)),
});

// Door
const equipmentSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  type: custom((v) => v, (v) => _toLower(v)),
  cameraStatus: custom((v) => v, (v) => _toLower(v)),
});
const doorEquipmentSchema = createSimpleSchema({
  equipment: object(equipmentSchema),
  usage: custom((v) => v, (v) => _toLower(v)),
});
export const doorSchema = createSimpleSchema({
  equipments: list(object(doorEquipmentSchema)),
  device: object(deviceSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  type: custom((v) => v, (v) => _toLower(v)),
  usage: custom((v) => v, (v) => _toLower(v)),
});

// Price
export const priceRuleSchema = createSimpleSchema({
  country: custom(() => SKIP, (v) => v),
  currency: custom(() => SKIP, (v) => v),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  taxRate: custom(() => SKIP, deserializePercentage),
});

// Synchronization management schemas
// Operations
const operationDeviceErrorSchema = createSimpleSchema({
  code: primitive(),
  desc: primitive(),
  ignored: primitive(),
});

const operationDeviceSchema = createSimpleSchema({
  device: object(deviceSchema),
  error: object(operationDeviceErrorSchema),
  status: custom((v) => v, (v) => _toLower(v)),
});

export const operationSchema = createSimpleSchema({
  devices: list(object(operationDeviceSchema)),
  id: custom((v) => v, (v) => _toFinite(v)),
  status: custom((v) => v, (v) => _toLower(v)),
});

// Synchronization status
const synchronizationStatusDeviceSchema = createSimpleSchema({
  device: object(deviceSchema),
  version: custom(() => SKIP, (v) => _toFinite(v)),
});

const synchronizationStatusSchema = createSimpleSchema({
  devices: list(object(synchronizationStatusDeviceSchema)),
  isSynchronized: primitive(),
  version: custom(() => SKIP, (v) => _toFinite(v)),
});

// ---- Models schemas ---- //
// Plannings
const planningSlotElementSchema = createSimpleSchema({
  dayOfWeek: custom((v) => v, (v) => _toLower(v)),
  time: primitive(),
});

const planningSlotSchema = createSimpleSchema({
  end: object(planningSlotElementSchema),
  start: object(planningSlotElementSchema),
});

export const planningSchema = createSimpleSchema({
  active: custom(() => SKIP, (v) => v),
  enabled: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  slots: list(object(planningSlotSchema)),
  synchronizationStatus: custom(() => SKIP, (v) => deserialize(synchronizationStatusSchema, v)),
});

// Car parks
export const carparkContactSchema = createSimpleSchema({
  email: primitive(),
  phone: primitive(),
});

const carparkSettingsOnDemandConfigurationSchema = createSimpleSchema({
  parkingCategory: custom(() => SKIP, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
  exitCourtesyDelay: custom(() => SKIP, (v) => _toFinite(v)),
  quickParkingCategory: custom(() => SKIP, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
});
const carparkSettingsOnDemandAutoCategorizationInputSchema = createSimpleSchema({
  parkingCategoryId: primitive(),
});
const carparkSettingsOnDemandSpecificCategoryInputSchema = createSimpleSchema({
  quickParkingCategoryId: primitive(),
});
export const defaultParkingCategorySchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  color: primitive(),
  name: primitive(),
});
export const carparkSettingsBookingSchema = createSimpleSchema({
  defaultParkingCategory: custom(() => SKIP, (v) => (v ? deserialize(defaultParkingCategorySchema, v) : null)),
  entryCourtesyDelay: primitive(),
  exitCourtesyDelay: primitive(),
  resetDelay: primitive(),
  maxDate: primitive(),
  maxDuration: primitive(),
  moduleEnabled: primitive(),
});
export const carparkSettingsGeneralSchema = createSimpleSchema({
  address: custom((v) => (v || SKIP), (v) => (v ? deserialize(addressSchema, v) : null)),
  email: primitive(),
  entryByLicensePlateReader: primitive(),
  exitByLicensePlateReader: primitive(),
  forbidManualKeypadCodes: primitive(),
  location: custom((v) => (v || SKIP), (v) => (v ? deserialize(locationSchema, v) : null)),
  phone: primitive(),
  maximumHeight: primitive(),
  accessSchedule: primitive(),
  authorizedVehicles: list(primitive()),
  allowLpg: primitive(),
  chargingStation: primitive(),
});
export const carparkSettingsOnDemandSchema = createSimpleSchema({
  autoCategorizationConfiguration: custom((v) => (v ? serialize(carparkSettingsOnDemandAutoCategorizationInputSchema, v) : undefined), () => SKIP),
  configuration: custom(() => SKIP, (v) => (v ? deserialize(carparkSettingsOnDemandConfigurationSchema, v) : null)),
  confirmationKeypadCode: primitive(),
  enabled: primitive(),
  capacityCheck: primitive(),
  temporaryExitMaxDuration: primitive(),
  exitCourtesyDelay: primitive(),
  dropOffDuration: primitive(),
  temporaryExitResetDelay: primitive(),
  ignoredLicensePlates: list(primitive()),
  mode: custom((v) => (v ? _toUpper(v) : null), (v) => (v ? _toLower(v) : null)),
  specificCategoryConfiguration: custom((v) => (v ? serialize(carparkSettingsOnDemandSpecificCategoryInputSchema, v) : undefined), () => SKIP),
});
export const carparkSettingsLicensePlateSchema = createSimpleSchema({
  recordCooldownEntryAfterEntry: primitive(),
  recordCooldownExitAfterExit: primitive(),
  recordCooldownExitAfterEntry: primitive(),
  recordCooldownEntryAfterExit: primitive(),
});

export const carparkSettingsSpacesSchema = createSimpleSchema({
  bookingCapacity: custom((v) => v, (v) => _toFinite(v)),
  assignedCapacity: custom((v) => v, (v) => _toFinite(v)),
});
const carparkSettingsSchema = createSimpleSchema({
  booking: custom(() => SKIP, (v) => deserialize(carparkSettingsBookingSchema, v)),
  general: custom(() => SKIP, (v) => deserialize(carparkSettingsGeneralSchema, v)),
  licensePlate: custom(() => SKIP, (v) => deserialize(carparkSettingsLicensePlateSchema, v)),
  onDemand: custom(() => SKIP, (v) => deserialize(carparkSettingsOnDemandSchema, v)),
  spaces: custom(() => SKIP, (v) => deserialize(carparkSettingsSpacesSchema, v)),
});

const carparkSlugSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  domain: primitive(),
  slug: primitive(),
});

export const carparkFinancialInformationSchema = createSimpleSchema({
  bookingCommission: custom(ignore, deserializePercentage),
  bookingServiceFeeRule: custom(ignore, (v) => v && deserialize(serviceFeeRuleSchema, v)),
  invoiceIdentifier: primitive(),
  onDemandCommission: custom(ignore, deserializePercentage),
  onDemandServiceFeeRule: custom(ignore, (v) => v && deserialize(serviceFeeRuleSchema, v)),
  paymentFrequency: custom(ignore, (v) => _toLower(v)),
});

export const updateCarparkFinancialInformationInputSchema = createSimpleSchema({
  bookingCommission: custom(serializePercentage, ignore),
  bookingServiceFeeRuleId: primitive(),
  onDemandCommission: custom(serializePercentage, ignore),
  onDemandServiceFeeRuleId: primitive(),
  paymentFrequency: custom((v) => _toUpper(v), ignore),
});

const carparkSynchronizationStatusesSchema = createSimpleSchema({
  bookings: primitive(),
  onDemandAccesses: primitive(),
  parkings: primitive(),
  parkingCategories: primitive(),
  plannings: primitive(),
});

// Simplified slug schema for workspaceGroup slugs (only fields needed from workspaceGroupFragment)
const carparkWorkspaceGroupSlugSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  domain: primitive(),
  slug: primitive(),
  usedOnPrintedQrCode: primitive(),
  comments: primitive(),
  usages: list(custom((v) => _toUpper(v), (v) => _toLower(v))),
});
const carparkWorkspaceGroupSlugsSchema = createSimpleSchema({
  entries: list(object(carparkWorkspaceGroupSlugSchema)),
});
const carparkWorkspaceGroupSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  slugs: custom(() => SKIP, (v) => (v ? deserialize(carparkWorkspaceGroupSlugsSchema, v) : null)),
  type: custom(() => SKIP, (v) => _toLower(v)),
  workspace: custom(() => SKIP, (v) => deserialize(workspaceSchema, v)),
});
const carparkWorkspaceGroupsListSchema = createSimpleSchema({
  entries: list(object(carparkWorkspaceGroupSchema)),
});
export const carparkSchema = createSimpleSchema({
  address: custom(() => SKIP, (v) => (v ? deserialize(addressSchema, v) : null)),
  allowListingOfOneparkBookings: primitive(),
  capacity: custom(() => SKIP, (v) => _toFinite(v)),
  contact: custom(() => SKIP, (v) => (v ? deserialize(carparkContactSchema, v) : null)),
  devices: list(object(deviceSchema)),
  doors: list(object(doorSchema)),
  financialInformation: custom(() => SKIP, (v) => (v ? deserialize(carparkFinancialInformationSchema, v) : null)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  location: custom(() => SKIP, (v) => (v ? deserialize(locationSchema, v) : null)),
  name: custom(() => SKIP, (v) => v),
  partner: custom(() => SKIP, (v) => (v ? deserialize(partnerSchema, v) : null)),
  permissions: custom(() => SKIP, (v) => _map(v, _toLower)),
  publicName: custom(() => SKIP, (v) => v),
  settings: custom(() => SKIP, (v) => deserialize(carparkSettingsSchema, v)),
  terminals: list(object(terminalSchema)),
  synchronizationStatuses: custom(() => SKIP, (v) => deserialize(carparkSynchronizationStatusesSchema, v)),
  slug: custom(() => SKIP, (v) => deserialize(carparkSlugSchema, v)),
  timezone: custom(() => SKIP, (v) => v),
  workspaceGroups: custom(() => SKIP, (v) => deserialize(carparkWorkspaceGroupsListSchema, v)),
  workspaceId: custom(() => SKIP, (v) => _toFinite(v)),
});

export const paginatedCarparksSchema = paginatedSchemaOf(carparkSchema);

export const carparkPublicSchema = createSimpleSchema({
  entryByLicensePlateReader: primitive(),
  exitByLicensePlateReader: primitive(),
  address: custom(() => SKIP, (v) => (v ? deserialize(addressSchema, v) : null)),
  contact: custom(() => SKIP, (v) => (v ? deserialize(carparkContactSchema, v) : null)),
  location: custom(() => SKIP, (v) => (v ? deserialize(locationSchema, v) : null)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  onDemandExitCourtesyDelay: custom(() => SKIP, (v) => v),
  onDemandTemporaryExitMaxDuration: custom(() => SKIP, (v) => v),
  publicName: custom(() => SKIP, (v) => v),
  slug: custom(() => SKIP, (v) => deserialize(slugSchema, v)),
});

// Parking categories
const parkingCategoryPriceGridAssociationSchema = createSimpleSchema({
  discount: custom(() => SKIP, deserializePercentage),
  priceGrid: custom(() => SKIP, (v) => (v ? deserialize(priceGridSchema, v) : null)),
  calculationMode: custom(() => SKIP, (v) => (v || 'PRORATED')),
});
const parkingCategoryAllotmentCapacitiesSchema = createSimpleSchema({
  max: custom(() => SKIP, (v) => _toFinite(v)),
  min: custom(() => SKIP, (v) => _toFinite(v)),
});

export const parkingCategoryAllotmentSchema = createSimpleSchema({
  assignedCapacities: object(parkingCategoryAllotmentCapacitiesSchema),
  bookingCapacities: object(parkingCategoryAllotmentCapacitiesSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  planning: custom(() => SKIP, (v) => (v ? deserialize(planningSchema, v) : null)),
  withoutBookingCapacities: object(parkingCategoryAllotmentCapacitiesSchema),
});
export const parkingCategorySchema = createSimpleSchema({
  color: primitive(),
  defaultAllotment: custom((v) => v, (v) => deserialize(parkingCategoryAllotmentSchema, v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  isOnepark: custom(() => SKIP, (v) => v),
  name: primitive(),
  priceGridAssociation: custom(() => SKIP, (v) => (v ? deserialize(parkingCategoryPriceGridAssociationSchema, v) : null)),
  synchronizationStatus: custom(() => SKIP, (v) => deserialize(synchronizationStatusSchema, v)),
  type: custom(() => SKIP, (v) => _toLower(v)),
});

// Price grids
const priceGridParkingCategoryAssociationSchema = createSimpleSchema({
  discount: custom(() => SKIP, deserializePercentage),
  calculationMode: custom(() => SKIP, (v) => (v || 'PRORATED')),
  parkingCategory: custom(() => SKIP, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
});
const priceGridAdditionalSchema = createSimpleSchema({
  amountInclTax: custom((v) => v, (v) => _toFinite(v)),
});
const priceGridTierSchema = createSimpleSchema({
  amountInclTax: custom((v) => v, (v) => _toFinite(v)),
  duration: custom((v) => v, (v) => _toFinite(v)),
  unit: custom((v) => _toUpper(v), (v) => _toLower(v)),
});
export const priceGridSchema = createSimpleSchema({
  active: custom(() => SKIP, (v) => v),
  additional: object(priceGridAdditionalSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  parkingCategoriesAssociations: custom(() => SKIP, (v) => (v ? deserialize(priceGridParkingCategoryAssociationSchema, v) : null)),
  priceRule: custom(() => SKIP, (v) => deserialize(priceRuleSchema, v)),
  priceRuleId: custom((v) => v, () => SKIP),
  tiers: list(object(priceGridTierSchema)),
});

// Offers
const offerCustomerSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
});

export const offerParkingSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
});

export const offerSchema = createSimpleSchema({
  amountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  amountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  begin: primitive(),
  customer: custom(() => SKIP, (v) => deserialize(offerCustomerSchema, v)),
  customerId: custom((v) => v, () => SKIP),
  discount: custom(() => SKIP, deserializePercentage),
  discountedAmountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  discountedAmountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  end: primitive(),
  expiresAt: custom(() => SKIP, (v) => v),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  parking: custom(() => SKIP, (v) => (v ? deserialize(offerParkingSchema, v) : null)),
  parkingCategory: custom(() => SKIP, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
  parkingCategoryId: custom((v) => v, (v) => _toFinite(v)),
  parkingId: custom((v) => v, () => SKIP),
  priceRule: custom(() => SKIP, (v) => deserialize(priceRuleSchema, v)),
  priceType: custom(() => SKIP, (v) => _toLower(v)),
  reference: custom(() => SKIP, (v) => v),
  spacesQuantity: custom((v) => v, (v) => _toFinite(v)),
  serviceFeeAmountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  serviceFeeAmountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  status: custom(() => SKIP, (v) => _toLower(v)),
  totalAmountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  totalAmountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  type: custom((v) => v, (v) => _toLower(v)),
});

// Parkings
const parkingVehicleSchema = createSimpleSchema({
  licensePlate: primitive(),
});

export const parkingStateSchema = createSimpleSchema({
  insideQuantity: custom((v) => v, (v) => _toFinite(v)),
  outsideQuantity: custom((v) => v, (v) => _toFinite(v)),
  outsideQuantityPermanentExit: custom((v) => v, (v) => _toFinite(v)),
  unknownQuantity: custom(() => SKIP, (v) => _toFinite(v)),
});

const parkingCustomerSchema = createSimpleSchema({
  email: primitive(),
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  lastname: primitive(),
  parkingCategory: custom(() => SKIP, (v) => deserialize(parkingCategorySchema, v)),
  phone: primitive(),
  spaceNumber: primitive(),
});

const paymentStripeDetailsErrorsSchema = createSimpleSchema({
  code: primitive(),
  date: primitive(),
  message: primitive(),
});

const paymentStripeDetailsSchema = createSimpleSchema({
  clientSecret: primitive(),
  errors: list(object(paymentStripeDetailsErrorsSchema)),
});

export const paymentSchema = createSimpleSchema({
  amount: primitive(),
  date: primitive(),
  provider: custom(() => SKIP, (v) => _toLower(v)),
  refundedAmount: primitive(),
  status: custom(() => SKIP, (v) => _toLower(v)),
  stripeDetails: object(paymentStripeDetailsSchema),
  target: custom(() => SKIP, (v) => _toLower(v)),
});

export const invoiceSchema = createSimpleSchema({
  amountExclTax: primitive(),
  amountInclTax: primitive(),
  canBeUpdated: primitive(),
  customerEmail: primitive(),
  customerFirstname: primitive(),
  customerLastname: primitive(),
  date: primitive(),
  id: alias('number', primitive()),
  number: primitive(),
  priceRule: custom(() => SKIP, (v) => deserialize(priceRuleSchema, v)),
  serviceFeeAmountExclTax: primitive(),
  serviceFeeAmountInclTax: primitive(),
  taxAmount: primitive(),
  type: custom(() => SKIP, (v) => _toLower(v)),
});

export const purchaseSchema = createSimpleSchema({
  amountExclTax: primitive(),
  amountInclTax: primitive(),
  discount: custom(() => SKIP, deserializePercentage),
  discountedAmountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  discountedAmountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  creationDate: primitive(),
  isRefundable: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  offers: list(object(offerSchema)),
  refundReason: custom(() => SKIP, (v) => _toLower(v)),
  refundInvoice: object(invoiceSchema),
  refundedAmount: primitive(),
  paidAmount: primitive(),
  paymentInvoice: custom(() => SKIP, (v) => (v ? deserialize(invoiceSchema, v) : null)),
  paymentStatus: custom(() => SKIP, (v) => _toLower(v)),
  payments: list(object(paymentSchema)),
  priceRule: custom(() => SKIP, (v) => deserialize(priceRuleSchema, v)),
  serviceFeeAmountExclTax: primitive(),
  serviceFeeAmountInclTax: primitive(),
  totalAmountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
  totalAmountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  type: custom((v) => (_toUpper(v) || SKIP), (v) => _toLower(v)),
});

export const parkingSchema = createSimpleSchema({
  begin: primitive(),
  comments: primitive(),
  customer: custom(() => SKIP, (v) => (v ? deserialize(parkingCustomerSchema, v) : null)),
  customerId: custom((v) => v, (v) => _toFinite(v)),
  doors: custom((v) => v, (v) => deserialize(doorSchema, v)),
  end: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  automaticKeypadCodesQuantity: primitive(),
  creationDate: primitive(),
  keypadCodes: custom((v) => (v || SKIP), (v) => (v || null)),
  parkingCategory: custom(() => SKIP, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
  planning: custom(() => SKIP, (v) => (v ? deserialize(planningSchema, v) : null)),
  planningId: custom((v) => v, (v) => _toFinite(v)),
  purchases: list(object(purchaseSchema)),
  reference: custom(() => SKIP, (v) => v),
  restrictedByAntipassbackEntrance: primitive(),
  restrictedByAntipassbackExit: primitive(),
  restrictedByForbiddenEntry: primitive(),
  restrictedByForbiddenExit: primitive(),
  restrictedByPermanentExit: primitive(),
  spacesQuantity: custom((v) => v, (v) => _toFinite(v)),
  state: object(parkingStateSchema),
  status: custom(() => SKIP, (v) => _toLower(v)),
  synchronizationStatus: custom(() => SKIP, (v) => deserialize(synchronizationStatusSchema, v)),
  functionalType: custom((v) => (_toUpper(v) || SKIP), (v) => _toLower(v)),
  inDropOff: primitive(),
  billingStatus: custom((v) => (_toUpper(v) || SKIP), (v) => _toLower(v)),
  type: custom((v) => (_toUpper(v) || SKIP), (v) => _toLower(v)),
  vehicles: list(object(parkingVehicleSchema)),
});

export const customerAccountShema = createSimpleSchema({
  status: custom(() => SKIP, (v) => _toLower(v)),
});

// Customers
export const customerSchema = createSimpleSchema({
  account: custom(() => SKIP, (v) => deserialize(customerAccountShema, v)),
  bookings: custom(() => SKIP, (v) => deserialize(bookingSchema, v)),
  comments: primitive(),
  parkingCategory: custom(() => SKIP, (v) => deserialize(parkingCategorySchema, v)),
  parkingCategoryId: custom((v) => v, (v) => _toFinite(v)),
  email: primitive(),
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  lastname: primitive(),
  phone: primitive(),
  spaceNumber: primitive(),
  parkings: custom(() => SKIP, (v) => deserialize(parkingSchema, v)),
});

// Onepark booking
export const bookingOneparkSchema = createSimpleSchema({
  id: alias('bookingId', primitive()),
  begin: primitive(),
  bookingId: primitive(),
  bookingUsage: custom(() => SKIP, (v) => _toLower(v)),
  customerFirstname: primitive(),
  customerLastname: primitive(),
  end: primitive(),
  keypadCode: primitive(),
  status: custom(() => SKIP, (v) => _toLower(v)),
  licensePlate: primitive(),
});

// Accesses
export const accessSchema = createSimpleSchema({
  date: primitive(),
  door: object(doorSchema),
  equipment: object(equipmentSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  oneparkBooking: object(bookingOneparkSchema),
  parking: object(parkingSchema),
  booking: custom(() => SKIP, (v) => (v ? deserialize(bookingSchema, v) : null)),
  onDemandAccess: custom(() => SKIP, (v) => (v ? deserialize(onDemandAccessSchema, v) : null)),
  ignored: primitive(),
  result: custom(() => SKIP, (v) => _toLower(v)),
  source: primitive(),
  type: custom(() => SKIP, (v) => _toLower(v)),
  usage: custom(() => SKIP, (v) => (v ? _toLower(v) : null)),
});

// On-demand access
const onDemandAccessParkingCategorySchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  type: custom((v) => v, (v) => _toLower(v)),
});

const onDemandAccessCustomerSchema = createSimpleSchema({
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  lastname: primitive(),
  parkingCategory: custom(() => SKIP, (v) => deserialize(onDemandAccessParkingCategorySchema, v)),
});

export const onDemandAccessSchema = createSimpleSchema({
  active: primitive(),
  begin: primitive(),
  customer: custom(() => SKIP, (v) => deserialize(onDemandAccessCustomerSchema, v)),
  email: primitive(),
  end: primitive(),
  keypadCode: primitive(),
  manualCreation: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  licensePlate: primitive(),
  parkingCategory: custom((v) => v, (v) => (v ? deserialize(onDemandAccessParkingCategorySchema, v) : null)),
  priceGrid: custom(() => SKIP, (v) => (v ? deserialize(priceGridSchema, v) : null)),
  purchases: list(object(purchaseSchema)),
  readLicensePlate: primitive(),
  spacesQuantity: custom((v) => v, (v) => _toFinite(v)),
  reference: primitive(),
  isDeletable: primitive(),
  isRefundable: primitive(),
  selfCheckoutType: custom(() => SKIP, (v) => (v ? _toLower(v) : null)),
  status: custom(() => SKIP, (v) => _toLower(v)),
  state: object(parkingStateSchema),
  synchronizationStatus: custom(() => SKIP, (v) => deserialize(synchronizationStatusSchema, v)),
});

export const onDemandAccessForCheckoutSchema = createSimpleSchema({
  active: primitive(),
  begin: primitive(),
  carpark: custom(() => SKIP, (v) => deserialize(carparkPublicSchema, v)),
  email: primitive(),
  end: primitive(),
  keypadCode: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  licensePlate: primitive(),
  magicLink: primitive(),
  priceGrid: custom(() => SKIP, (v) => (v ? deserialize(priceGridSchema, v) : null)),
  purchases: list(object(purchaseSchema)),
  reference: primitive(),
  selfCheckoutType: custom(() => SKIP, (v) => (v ? _toLower(v) : null)),
  status: custom(() => SKIP, (v) => _toLower(v)),
});

// Metrics
export const metricOccupancyLiveSchema = createSimpleSchema({
  occupancy: primitive(),
  capacity: primitive(),
  rate: custom(() => SKIP, (v) => _toFinite(v)),
});

const metricOccupancyPeriodRateSchema = createSimpleSchema({
  average: custom(() => SKIP, (v) => _toFinite(v)),
  max: custom(() => SKIP, (v) => _toFinite(v)),
  min: custom(() => SKIP, (v) => _toFinite(v)),
});

const metricOccupancyCountSchema = createSimpleSchema({
  average: custom(() => SKIP, (v) => _toFinite(v)),
  max: custom(() => SKIP, (v) => _toFinite(v)),
  min: custom(() => SKIP, (v) => _toFinite(v)),
});

export const metricOccupancyPeriodSchema = createSimpleSchema({
  date: primitive(),
  rate: object(metricOccupancyPeriodRateSchema),
  count: custom(() => SKIP, (v) => (v ? deserialize(metricOccupancyCountSchema, v) : null)),
});

const metricAccessesQuantitiesSchema = createSimpleSchema({
  input: custom(() => SKIP, (v) => _toFinite(v)),
  output: custom(() => SKIP, (v) => _toFinite(v)),
  na: custom(() => SKIP, (v) => _toFinite(v)),
});
export const metricAccessesSchema = createSimpleSchema({
  date: primitive(),
  quantities: object(metricAccessesQuantitiesSchema),
});

const metricRevenuesValueSchema = createSimpleSchema({
  amountInclTax: custom(() => SKIP, (v) => _toFinite(v)),
  amountExclTax: custom(() => SKIP, (v) => _toFinite(v)),
});
export const metricRevenuesSchema = createSimpleSchema({
  date: primitive(),
  value: object(metricRevenuesValueSchema),
});

// Booking
export const bookingSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  begin: primitive(),
  carpark: custom((v) => v, (v) => deserialize(carparkSchema, v)),
  comments: primitive(),
  customer: custom((v) => v, (v) => deserialize(customerSchema, v)),
  doors: custom((v) => v, (v) => deserialize(doorSchema, v)),
  end: primitive(),
  isRefundable: primitive(),
  creationDate: primitive(),
  keypadCodes: list(primitive()),
  parkingCategory: custom((v) => v, (v) => (v ? deserialize(parkingCategorySchema, v) : null)),
  purchases: list(object(purchaseSchema)),
  reference: primitive(),
  restrictedByAntipassbackEntrance: primitive(),
  restrictedByAntipassbackExit: primitive(),
  restrictedByForbiddenEntry: primitive(),
  restrictedByForbiddenExit: primitive(),
  restrictedByPermanentExit: primitive(),
  spacesQuantity: custom((v) => v, (v) => _toFinite(v)),
  state: object(parkingStateSchema),
  status: custom(() => SKIP, (v) => _toLower(v)),
  synchronizationStatus: custom(() => SKIP, (v) => deserialize(synchronizationStatusSchema, v)),
  vehicles: list(object(parkingVehicleSchema)),
});

// Extend booking
export const extendBookingSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  begin: primitive(),
  end: primitive(),
  carpark: custom((v) => v, (v) => deserialize(carparkSchema, v)),
  keypadCodes: list(primitive()),
  purchases: list(object(purchaseSchema)),
  reference: primitive(),
  status: custom(() => SKIP, (v) => _toLower(v)),
  vehicles: list(object(parkingVehicleSchema)),
});

export const bookingAddSchema = createSimpleSchema({
  automaticKeypadCodesQuantity: primitive(),
  comments: primitive(),
  doors: custom((v) => v, (v) => deserialize(doorSchema, v)),
  keypadCodes: custom((v) => (v || SKIP), (v) => (v || null)),
  offerReference: primitive(),
  restrictedByAntipassbackEntrance: primitive(),
  restrictedByAntipassbackExit: primitive(),
  restrictedByPermanentExit: primitive(),
  sendPaymentLinkToCustomer: primitive(),
  state: object(parkingStateSchema),
  vehicles: list(object(parkingVehicleSchema)),
});

export const bookingUpdateSchema = createSimpleSchema({
  comments: primitive(),
  doors: custom((v) => v, (v) => deserialize(doorSchema, v)),
  keypadCodes: custom((v) => (v || SKIP), (v) => (v || null)),
  restrictedByAntipassbackEntrance: primitive(),
  restrictedByAntipassbackExit: primitive(),
  restrictedByForbiddenEntry: primitive(),
  restrictedByForbiddenExit: primitive(),
  restrictedByPermanentExit: primitive(),
  vehicles: list(object(parkingVehicleSchema)),
});

export const bookingInputSchema = createSimpleSchema({
  offerReference: primitive(),
  vehicles: object(parkingVehicleSchema),
});

export const extendBookingInputSchema = createSimpleSchema({
  carparkId: primitive(),
  id: primitive(),
  offerReference: primitive(),
  sendPaymentLinkToCustomer: primitive(),
});

export const carparkBookingOptionSchema = createSimpleSchema({
  customerIds: list(primitive()),
  parkingCategoryId: custom(() => SKIP, (v) => _toFinite(v)),
  priceGrid: custom(() => SKIP, (v) => (v ? deserialize(priceGridSchema, v) : null)),
});

const bookingCarparkSearchInputSchema = createSimpleSchema({
  carparkId: custom((v) => v, (v) => _toFinite(v)),
  customerId: custom((v) => v, (v) => _toFinite(v)),
  parkingCategoryId: custom((v) => v, (v) => _toFinite(v)),
});
export const bookingSearchInputSchema = createSimpleSchema({
  begin: primitive(),
  carpark: object(bookingCarparkSearchInputSchema),
  discount: custom(serializePercentage, () => SKIP),
  end: primitive(),
  spacesQuantity: custom((v) => v, (v) => _toFinite(v)),
});

export const bookingExtendSearchInputSchema = createSimpleSchema({
  carparkId: primitive(),
  end: primitive(),
  id: primitive(),
});

export const bookingPayOverstayInputSchema = createSimpleSchema({
  carparkId: primitive(),
  id: primitive(),
});

export const bookingPayOverstaySchema = createSimpleSchema({
  purchases: list(object(purchaseSchema)),
});

// Account
const accountCarparkSchema = createSimpleSchema({
  id: custom((v) => v, (v) => _toFinite(v)),
  name: primitive(),
  publicName: primitive(),
});

/* Sign-up */
const signUpExistingAccountSchema = createSimpleSchema({
  email: primitive(),
  password: primitive(),
});
const signUpNewAccountSchema = createSimpleSchema({
  email: primitive(),
  firstname: primitive(),
  lastname: primitive(),
  locale: primitive(),
  password: primitive(),
  passwordConfirmation: primitive(),
  phone: primitive(),
});

export const signUpSchema = createSimpleSchema({
  existingAccount: object(signUpExistingAccountSchema),
  newAccount: object(signUpNewAccountSchema),
  token: primitive(),
});

/* List Accounts */
const accountHistorySchema = createSimpleSchema({
  cause: custom(() => SKIP, (v) => (v ? _toLower(v) : null)),
  comments: primitive(),
  date: primitive(),
  status: custom((v) => v, (v) => _toLower(v)),
});
const accountAuthorizationSchema = createSimpleSchema({
  id: custom((v) => v, (v) => _toFinite(v)),
  carpark: object(accountCarparkSchema),
  cause: custom(() => SKIP, (v) => (v ? _toLower(v) : null)),
  history: list(object(accountHistorySchema)),
  status: custom((v) => v, (v) => _toLower(v)),
});
export const accountSchema = createSimpleSchema({
  id: custom((v) => v, (v) => _toFinite(v)),
  authorizations: list(object(accountAuthorizationSchema)),
  email: primitive(),
  firstname: primitive(),
  history: list(object(accountHistorySchema)),
  lastname: primitive(),
});

/* Set Account Status */
export const accountSetStatusSchema = createSimpleSchema({
  authorizationId: custom((v) => v, (v) => _toFinite(v)),
  cause: custom((v) => (v ? _toUpper(v) : null), (v) => (v ? _toLower(v) : null)),
  comments: primitive(),
  id: custom((v) => v, (v) => _toFinite(v)),
  status: primitive(),
});

// OnDemand Access
export const onDemandAccessSelfCheckoutSchema = createSimpleSchema({
  estimatedEnd: primitive(),
  locale: primitive(),
  reference: primitive(),
  type: custom((v) => _toUpper(v), (v) => _toLower(v)),
});
export const onDemandAccessExtendCheckoutSchema = createSimpleSchema({
  emailPrefix: primitive(),
  estimatedEnd: primitive(),
  reference: primitive(),
});
export const onDemandAccessOverstayCheckoutSchema = createSimpleSchema({
  carpark: primitive(),
  emailPrefix: primitive(),
  reference: primitive(),
});

// Partners
export const partnerFinancialInformationSchema = createSimpleSchema({
  address: custom((v) => v, (v) => deserialize(addressSchema, v)),
  emailForCommissionInvoices: primitive(),
  europeanVatNumber: primitive(),
  iban: primitive(),
  invoiceIdentifier: primitive(),
  nationalIdentifier: primitive(),
});
export const partnerSchema = createSimpleSchema({
  financialInformation: object(partnerFinancialInformationSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});

// Workspace
export const workspaceSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});
export const workSpaceGroupCarparksSchema = createSimpleSchema({
  entries: list(object(carparkSchema)),
});
export const workSpaceGroupSchema = createSimpleSchema({
  addedCarparkIds: custom((v) => (v || SKIP), () => SKIP),
  carparks: custom(() => SKIP, (v) => deserialize(workSpaceGroupCarparksSchema, v)),
  deletedCarparkIds: custom((v) => (v || SKIP), () => SKIP),
  id: custom((v) => _toFinite(v), (v) => _toFinite(v)),
  name: primitive(),
  type: custom(() => SKIP, (v) => _toLower(v)),
  workspaceId: custom((v) => _toFinite(v), () => SKIP),
});
const slugCustomizationSchema = createSimpleSchema({
  hideHeaderLogo: primitive(),
  logo: primitive(),
  showOnDemandCheckoutTutorial: primitive(),
  title: primitive(),
});
export const slugSchema = createSimpleSchema({
  comments: primitive(),
  customization: custom(() => SKIP, (v) => (v ? deserialize(slugCustomizationSchema, v) : null)),
  domain: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  slug: primitive(),
  type: custom(() => SKIP, (v) => _toLower(v)),
  usages: list(custom((v) => _toUpper(v), (v) => _toLower(v))),
  usedOnPrintedQrCode: primitive(),
  workspace: custom(() => SKIP, (v) => (v ? deserialize(workspaceSchema, v) : null)),
  workspaceGroup: custom(() => SKIP, (v) => (v ? deserialize(workSpaceGroupSchema, v) : null)),
});

// Service fee rule
export const serviceFeeRuleSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  carparks: custom(() => SKIP, (v) => (v ? deserialize(paginatedCarparksSchema, v) : null)),
  maximumAmount: custom(serializeAmount, deserializeAmount),
  minimumAmount: custom(serializeAmount, deserializeAmount),
  name: primitive(),
  priceThreshold: custom(serializeAmount, deserializeAmount),
  value: custom(serializePercentage, deserializePercentage),
});

export const paginatedServiceFeeRuleSchema = paginatedSchemaOf(serviceFeeRuleSchema);



// WEBPACK FOOTER //
// ./models/cpm/schemas/index.js